import {
  useFetch,
  FetchMethod,
  FetchResult,
  FetchFnParam,
  UrlBuilderParams,
} from "./useFetch"
import { useCallback } from "react"
import { API_PREFIX } from "@/constants"
import useSysConfig from "./useSysConfig"
import i18n from "@/i18n"

function getCodeerApiRequestLocale(): string {
  return (
    i18n.resolvedLanguage ||
    i18n.language ||
    navigator.languages?.[0] ||
    navigator.language ||
    "en"
  )
}

function withDefaultAcceptLanguage<T, K>(
  params?: FetchFnParam<T, K>
): FetchFnParam<T, K> {
  const headers = { ...(params?.headers ?? {}) }
  const hasAcceptLanguage = Object.keys(headers).some(
    (name) => name.toLowerCase() === "accept-language"
  )
  if (!hasAcceptLanguage) {
    headers["Accept-Language"] = getCodeerApiRequestLocale()
  }
  return { ...params, headers }
}

export function useCodeerApi<
  T,
  K = Record<string, string | number | boolean | object>,
>(
  url: string,
  method: FetchMethod = FetchMethod.Get,
  json: boolean = true,
  urlBuilder?: (params: UrlBuilderParams) => string
): FetchResult<T, K> {
  const config = useSysConfig()
  // Keep the derived URL builder stable so useFetch does not recreate fetchFn on
  // every render for dynamic endpoints that rely on urlBuilderParams.
  const codeerUrlBuilder = useCallback(
    (params: UrlBuilderParams) => {
      return `${config.serverRoot}${API_PREFIX}${urlBuilder?.(params) ?? url}`
    },
    [config.serverRoot, url, urlBuilder]
  )
  const result = useFetch<T, K>(
    `${config.serverRoot}${API_PREFIX}${url}`,
    method,
    json,
    urlBuilder ? codeerUrlBuilder : undefined
  )
  const baseFetchFn = result.fetchFn
  const fetchFn = useCallback(
    (params?: FetchFnParam<T, K>) =>
      baseFetchFn(withDefaultAcceptLanguage(params)),
    [baseFetchFn]
  )

  return { ...result, fetchFn }
}

export * from "./useFetch"
